import { Link } from "wouter";
import { useModals } from "../common/ModalsProvider.js";
import { useT } from "../../i18n/index.js";
import { trpc } from "../../lib/trpc.js";
import { useFmtPrazo } from "./MyTasksList.js";

/**
 * F-recados-equipe (CEO 2026-10-28) — pop-up dos recados do escritório ao
 * entrar no Gestion. Dois botões por card: "Ciente" fecha até a próxima
 * entrada na conta (o servidor compara com o login); "Feito" encerra.
 *
 * F-tarefas-popup (CEO 2026-10-29) — as TAREFAS atrasadas/de hoje entram no
 * mesmo pop-up, em cima ("✅ Tarefas a realizar"): "Feito" fecha a ocorrência;
 * "Ver depois" esconde até a próxima entrada. Recados ficam embaixo.
 */
export type TeamNotice = {
  id: number;
  companyName: string;
  message: string;
  showFrom: Date | string;
  createdByName: string | null;
  repeats?: boolean;
  /** F-informativo — um botão só ("Ciente" encerra). */
  info?: boolean;
};

export type PopupTask = {
  occurrenceId: number | null;
  taskId: number;
  title: string;
  description: string | null;
  priority: string;
  dueAt: string;
  bucket: "overdue" | "today" | "upcoming";
  companyName: string;
  createdByName: string | null;
  recurring: boolean;
};

export function TeamNoticesPopup({
  notices,
  tasks = [],
  open,
  onClose,
  tasksHref = "/tarefas",
}: {
  notices: TeamNotice[];
  tasks?: PopupTask[];
  open: boolean;
  onClose: () => void;
  /** Link "Abrir Minhas tarefas" (vazio = sem link, ex.: portal do chantier). */
  tasksHref?: string | null;
}) {
  const t = useT();
  const fmtPrazo = useFmtPrazo();
  const modals = useModals();
  const utils = trpc.useUtils();
  const refetch = () => void utils.erpNotices.mine.invalidate();
  const refetchTasks = () => void utils.tasks.invalidate();
  const ackMut = trpc.erpNotices.ack.useMutation({ onSuccess: refetch });
  const doneMut = trpc.erpNotices.done.useMutation({ onSuccess: refetch });
  const taskDoneMut = trpc.tasks.complete.useMutation({ onSuccess: refetchTasks });
  const snoozeMut = trpc.tasks.snooze.useMutation({ onSuccess: refetchTasks });
  const tarefas = tasks.filter((x) => x.occurrenceId != null);
  if (!open || (notices.length === 0 && tarefas.length === 0)) return null;
  const busy = ackMut.isPending || doneMut.isPending;
  const taskBusy = taskDoneMut.isPending || snoozeMut.isPending;
  const comTarefas = tarefas.length > 0;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={comTarefas ? t("teamNotices.popupTasksTitle") : t("teamNotices.popupTitle")}
      data-testid="team-notices-popup"
    >
      {/* Fundo sólido pelos tokens do tema ("bg-surface" não existe no @theme e
          o cartão ficava transparente — no portal do chantier, por cima da página). */}
      <div
        className="mt-10 w-full max-w-lg space-y-3 rounded-lg border p-5 shadow-xl"
        style={{
          background: "var(--surface-card, #ffffff)",
          color: "var(--text-strong, #15203a)",
          borderColor: "var(--border, #dde2e7)",
        }}
      >
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-base font-bold" data-testid="team-popup-title">
            {comTarefas
              ? `✅ ${t("teamNotices.popupTasksTitle")}`
              : `ℹ️ ${t("teamNotices.popupTitle")}`}
          </h2>
          <button
            type="button"
            onClick={async () => {
              // CEO 2026-10-31 — fechar com tarefas na tela pede confirmação:
              // "Sim" fecha; "Não" mantém o pop-up aberto.
              if (comTarefas) {
                const ok = await modals.confirm({
                  title: t("teamNotices.closeConfirmTitle"),
                  message: t("teamNotices.closeConfirmMsg"),
                  confirmLabel: t("teamNotices.closeConfirmYes"),
                  cancelLabel: t("teamNotices.closeConfirmNo"),
                });
                if (!ok) return;
              }
              onClose();
            }}
            className="text-xl leading-none text-text-muted"
            aria-label={t("teamNotices.closeBtn")}
            data-testid="team-popup-close"
          >
            ×
          </button>
        </div>

        {comTarefas && (
          <div className="space-y-2" data-testid="popup-tasks">
            <p className="text-xs text-text-muted">{t("teamNotices.popupTasksHint")}</p>
            {(taskDoneMut.error ?? snoozeMut.error) && (
              <p className="text-xs text-red-500">
                {(taskDoneMut.error ?? snoozeMut.error)!.message}
              </p>
            )}
            {tarefas.map((r) => {
              const atrasada = r.bucket === "overdue";
              return (
                <div
                  key={r.occurrenceId}
                  className="space-y-1.5 rounded-md border px-3 py-3"
                  style={
                    atrasada
                      ? { background: "#FEE2E2", borderColor: "#DC2626", color: "#7F1D1D" }
                      : { background: "#EDE9FE", borderColor: "#7C3AED", color: "#3B0764" }
                  }
                  data-testid="popup-task"
                >
                  <p className="text-xs font-semibold uppercase tracking-wider">
                    {atrasada ? `⚠ ${t("tasks.st_overdue")}` : t("tasks.groupToday")} ·{" "}
                    {t("tasks.dueBy")} {fmtPrazo(r.dueAt)}
                  </p>
                  <p className="text-sm font-semibold">
                    {r.priority === "urgent" && (
                      <span className="mr-1.5 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
                        {t("tasks.urgent")}
                      </span>
                    )}
                    {r.title}
                  </p>
                  {r.description && (
                    <p className="whitespace-pre-line text-xs opacity-90">{r.description}</p>
                  )}
                  <button
                    type="button"
                    disabled={taskBusy}
                    onClick={() => taskDoneMut.mutate({ occurrenceId: r.occurrenceId! })}
                    className="rounded-md border px-3 py-1 text-xs font-semibold disabled:opacity-40"
                    style={{ background: "#DCFCE7", borderColor: "#16A34A", color: "#14532D" }}
                    data-testid="popup-task-done"
                  >
                    ✔ {t("tasks.doneBtn")}
                  </button>
                </div>
              );
            })}
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={taskBusy}
                onClick={() =>
                  snoozeMut.mutate(
                    { occurrenceIds: tarefas.map((r) => r.occurrenceId!) },
                    { onSuccess: () => notices.length === 0 && onClose() },
                  )
                }
                className="rounded-md border px-3 py-1 text-xs font-semibold disabled:opacity-40"
                style={{ borderColor: "var(--border, #dde2e7)" }}
                data-testid="popup-tasks-later"
              >
                ⏰ {t("teamNotices.laterBtn")}
              </button>
              {tasksHref && (
                <Link href={tasksHref} onClick={onClose} className="text-xs underline">
                  {t("teamNotices.popupOpenTasks")} ›
                </Link>
              )}
            </div>
          </div>
        )}

        {notices.length > 0 && (
          <div className="space-y-2">
            {comTarefas ? (
              <h3 className="pt-1 text-sm font-bold">ℹ️ {t("teamNotices.popupNoticesSub")}</h3>
            ) : null}
            {notices.some((n) => !n.info) && (
              <p className="text-xs text-text-muted">{t("teamNotices.popupHint")}</p>
            )}
            {notices.map((n) => (
              <div
                key={n.id}
                className="space-y-2 rounded-md border px-3 py-3"
                style={{ background: "#E0F2FE", borderColor: "#0284C7", color: "#0C4A6E" }}
                data-testid="team-notice"
              >
                <p className="text-xs font-semibold uppercase tracking-wider">
                  {n.companyName} · {new Date(n.showFrom).toLocaleDateString("fr-FR")}
                  {n.createdByName ? ` · ${n.createdByName}` : ""}
                </p>
                <p className="whitespace-pre-line text-sm">{n.message}</p>
                {n.info ? (
                  /* Informativo: um botão só — "Ciente" encerra (CEO 2026-10-29). */
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => doneMut.mutate({ noticeId: n.id })}
                    className="rounded-md border px-3 py-1 text-xs font-semibold disabled:opacity-40"
                    style={{ borderColor: "#0284C7", background: "#FFFFFF", color: "#0C4A6E" }}
                    data-testid="team-notice-info-ok"
                  >
                    ✓ {t("teamNotices.ackBtn")}
                  </button>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => ackMut.mutate({ noticeId: n.id })}
                      className="rounded-md border px-3 py-1 text-xs font-semibold disabled:opacity-40"
                      style={{ borderColor: "#0284C7", background: "#FFFFFF", color: "#0C4A6E" }}
                      data-testid="team-notice-ack"
                    >
                      ✓ {t("teamNotices.ackBtn")}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => doneMut.mutate({ noticeId: n.id })}
                      className="rounded-md border px-3 py-1 text-xs font-semibold disabled:opacity-40"
                      style={{ background: "#DCFCE7", borderColor: "#16A34A", color: "#14532D" }}
                      data-testid="team-notice-done"
                    >
                      ✔ {t("teamNotices.doneBtn")}
                    </button>
                  </div>
                )}
                {n.repeats && (
                  <p className="text-[11px] opacity-80">↻ {t("teamNotices.repeatDoneHint")}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
